import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    const body = new URLSearchParams({ email });
    await fetch('https://readdy.ai/api/form/d7e1abckpdh0a4ae08ng', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    setSubmitting(false);
    setSubmitted(true);
    setEmail('');
  };

  return (
    <footer className="w-full">
      {/* Newsletter Banner */}
      <div className="relative w-full h-64 md:h-80 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/6bf76ba6c4e12981ec2873d1e2cd0bd7.jpg"
          alt="Vikings Stadium"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#4a1a6b]/90 via-[#4a1a6b]/60 to-transparent flex items-center px-8 md:px-16">
          <div className="max-w-lg">
            <p className="text-white/70 text-xs tracking-widest uppercase mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Stay in Touch</p>
            <h3 className="text-white text-2xl md:text-3xl font-black uppercase mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Sign up to hear the latest Vikings news
            </h3>
            {submitted ? (
              <p className="text-[#ffd700] font-bold text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Thanks for subscribing! Skol Vikings!
              </p>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-3 rounded-full bg-white/20 text-white placeholder-white/60 text-sm border border-white/30 focus:outline-none focus:border-[#ffd700] backdrop-blur-sm"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="whitespace-nowrap px-6 py-3 rounded-full bg-[#ffd700] text-[#4a1a6b] font-bold text-sm tracking-widest uppercase cursor-pointer hover:bg-white transition-all duration-300 disabled:opacity-60"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {submitting ? 'Subscribing...' : 'Subscribe Now'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="bg-[#2a0a3d] text-white px-6 md:px-12 py-14">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <h4 className="text-2xl font-black uppercase text-[#ffd700] mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>Purple Pride</h4>
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-xs">
              The home of Minnesota Vikings fans worldwide. Bringing the purple and gold community together since 1998.
            </p>
            <div className="flex items-center gap-4">
              {['ri-facebook-fill', 'ri-twitter-x-fill', 'ri-instagram-line', 'ri-youtube-fill'].map((icon) => (
                <a key={icon} href="#" rel="nofollow" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#ffd700] hover:text-[#4a1a6b] transition-all cursor-pointer">
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Nav Links */}
          <div>
            <h5 className="text-xs font-bold tracking-widest uppercase text-white/50 mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>Navigation</h5>
            <ul className="flex flex-col gap-3">
              {[
                { label: 'Home', path: '/' },
                { label: 'Forums', path: '/forums' },
                { label: 'News', path: '/news' },
                { label: 'Links', path: '/links' },
                { label: 'Team', path: '/team' },
                { label: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/70 text-sm hover:text-[#ffd700] transition-colors cursor-pointer underline" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h5 className="text-xs font-bold tracking-widest uppercase text-white/50 mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>Contact</h5>
            <ul className="flex flex-col gap-3 text-sm text-white/70" style={{ fontFamily: "'Oswald', sans-serif" }}>
              <li className="flex items-center gap-2"><i className="ri-mail-line text-[#ffd700]"></i> info@purplepride.org</li>
              <li className="flex items-center gap-2"><i className="ri-map-pin-line text-[#ffd700]"></i> Minneapolis, MN</li>
              <li className="flex items-center gap-2"><i className="ri-global-line text-[#ffd700]"></i> purplepride.org</li>
            </ul>
            <div className="mt-6">
              <h5 className="text-xs font-bold tracking-widest uppercase text-white/50 mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>Management</h5>
              <p className="text-white/50 text-xs leading-relaxed">
                Tailgate Management by NeuralDog<br />
                USA Today Sports Media Group
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs" style={{ fontFamily: "'Oswald', sans-serif" }}>
            &copy; 1998–2026 by Purplepride.org. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" rel="nofollow" className="text-white/40 text-xs hover:text-white/70 transition-colors cursor-pointer underline" style={{ fontFamily: "'Oswald', sans-serif" }}>Privacy Policy</a>
            <a href="#" rel="nofollow" className="text-white/40 text-xs hover:text-white/70 transition-colors cursor-pointer underline" style={{ fontFamily: "'Oswald', sans-serif" }}>Terms of Use</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
